import { Link } from 'react-router-dom';
import { ArrowRight, X, Check, Minus } from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';
import { FooterSection } from '../components/sections';

interface ComparisonRow {
    category: string;
    manual: string;
    forecaster: string;
    manualStatus: 'bad' | 'neutral';
    forecasterStatus: 'good';
}

const comparisons: ComparisonRow[] = [
    {
        category: 'Scaling approach',
        manual: 'Reactive -- HPA triggers after load arrives',
        forecaster: 'Predictive scaling ahead of demand',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'Cold starts',
        manual: '10-60 seconds per GPU pod startup',
        forecaster: 'Eliminated -- capacity ready before traffic arrives',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'GPU utilization',
        manual: '15-30% typical (over-provisioned for peak)',
        forecaster: '60-85% optimized (right-sized continuously)',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'Multi-region routing',
        manual: 'Single provider, single region, manual failover',
        forecaster: 'Multi-Region Intelligence across 12 providers, 30+ regions, compliance-aware',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'Cost optimization',
        manual: 'Over-provisioning for peak headroom',
        forecaster: 'Right-sized with spot, overflow, and arbitrage',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'SLO compliance',
        manual: 'Best effort, manual monitoring',
        forecaster: 'Policy-enforced with automatic remediation',
        manualStatus: 'neutral',
        forecasterStatus: 'good',
    },
    {
        category: 'Setup time',
        manual: 'Weeks of custom tooling and tuning',
        forecaster: '5 minutes -- one Helm install',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'Ongoing maintenance',
        manual: 'Continuous manual tuning and firefighting',
        forecaster: 'Autonomous optimization, self-adjusting',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
    {
        category: 'Energy optimization',
        manual: 'No visibility into energy per inference',
        forecaster: 'Per-stage energy routing with carbon awareness',
        manualStatus: 'bad',
        forecasterStatus: 'good',
    },
];

const StatusIcon = ({ status }: { status: 'bad' | 'neutral' | 'good' }) => {
    if (status === 'good') return <Check className="w-4 h-4 text-volt-400" />;
    if (status === 'bad') return <X className="w-4 h-4 text-red-400/60" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
};

export const Comparison = () => {
    return (
        <div className="min-h-screen bg-obsidian-900 text-white antialiased">
            {/* Navbar */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <ForecasterLogo />
                    <Link
                        to="/trial"
                        className="px-5 py-2.5 bg-volt-500 text-obsidian-900 rounded-lg text-sm font-semibold hover:bg-volt-400 transition-all hover:shadow-volt"
                    >
                        Start Free Trial
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-32 pb-16 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-volt-500/5 rounded-full blur-[150px]" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                    <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                        Manual scaling vs. Forecaster
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Most GPU infrastructure runs on reactive autoscaling built for CPU workloads. Here is how predictive, GPU-native optimization changes the equation.
                    </p>
                </div>
            </section>

            {/* Comparison Table - Desktop */}
            <section className="pb-20">
                <div className="max-w-5xl mx-auto px-6">
                    {/* Desktop table */}
                    <div className="hidden md:block rounded-2xl border border-white/10 overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-[1fr_1fr_1fr] bg-white/[0.04]">
                            <div className="p-5 border-r border-white/5">
                                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Capability</span>
                            </div>
                            <div className="p-5 border-r border-white/5">
                                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Manual / HPA</span>
                            </div>
                            <div className="p-5">
                                <span className="text-sm font-semibold text-volt-400 uppercase tracking-wide">Forecaster</span>
                            </div>
                        </div>

                        {/* Rows */}
                        {comparisons.map((row, i) => (
                            <div
                                key={row.category}
                                className={`grid grid-cols-[1fr_1fr_1fr] ${
                                    i < comparisons.length - 1 ? 'border-b border-white/5' : ''
                                }`}
                            >
                                <div className="p-5 border-r border-white/5">
                                    <span className="text-white font-medium text-sm">{row.category}</span>
                                </div>
                                <div className="p-5 border-r border-white/5 flex items-start gap-3">
                                    <StatusIcon status={row.manualStatus} />
                                    <span className="text-gray-400 text-sm">{row.manual}</span>
                                </div>
                                <div className="p-5 flex items-start gap-3 bg-volt-500/[0.02]">
                                    <StatusIcon status={row.forecasterStatus} />
                                    <span className="text-gray-300 text-sm">{row.forecaster}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-4">
                        {comparisons.map((row) => (
                            <div key={row.category} className="rounded-xl border border-white/10 overflow-hidden">
                                <div className="px-5 py-3 bg-white/[0.04] border-b border-white/5">
                                    <span className="text-white font-medium text-sm">{row.category}</span>
                                </div>
                                <div className="divide-y divide-white/5">
                                    <div className="px-5 py-4 flex items-start gap-3">
                                        <StatusIcon status={row.manualStatus} />
                                        <div>
                                            <span className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Manual / HPA</span>
                                            <span className="text-gray-400 text-sm">{row.manual}</span>
                                        </div>
                                    </div>
                                    <div className="px-5 py-4 flex items-start gap-3 bg-volt-500/[0.02]">
                                        <StatusIcon status={row.forecasterStatus} />
                                        <div>
                                            <span className="text-xs text-volt-400 uppercase tracking-wide block mb-1">Forecaster</span>
                                            <span className="text-gray-300 text-sm">{row.forecaster}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Summary Stats */}
            <section className="pb-20">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 text-center">
                            <div className="text-3xl font-bold text-volt-400 mb-2">Significantly</div>
                            <div className="text-sm text-gray-400">Higher GPU utilization</div>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 text-center">
                            <div className="text-3xl font-bold text-volt-400 mb-2">Up to 60%</div>
                            <div className="text-sm text-gray-400">Cost reduction</div>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 text-center">
                            <div className="text-3xl font-bold text-volt-400 mb-2">5 min</div>
                            <div className="text-sm text-gray-400">Setup time vs. weeks</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detailed breakdown */}
            <section className="pb-20">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8 text-center">
                        Why reactive scaling breaks for GPUs
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                            <h3 className="text-white font-bold mb-3">GPUs are not CPUs</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                CPU pods start in milliseconds. GPU pods take 10-60 seconds to load model weights into VRAM. By the time HPA detects load and spins up capacity, the traffic spike has already caused timeouts and SLO violations.
                            </p>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                            <h3 className="text-white font-bold mb-3">Over-provisioning is the default</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Without predictive scaling, teams over-provision 2-3x to absorb spikes. This drives GPU utilization down to 15-30% and inflates cloud bills. Forecaster predicts demand ahead and scales proactively.
                            </p>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                            <h3 className="text-white font-bold mb-3">Single cloud is a trap</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                GPU pricing varies 30-50% across providers at any given moment. Manual provider management means you are always overpaying somewhere. Automatic routing captures arbitrage opportunities in real time.
                            </p>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                            <h3 className="text-white font-bold mb-3">Manual tuning does not scale</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                As GPU fleets grow, the engineering effort to tune autoscaling, manage spot interruptions, and optimize batch scheduling grows linearly. Autonomous optimization handles this continuously without human intervention.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="p-10 rounded-2xl bg-gradient-to-br from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/20">
                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                            Try it yourself
                        </h2>
                        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                            Install Forecaster on your cluster in 5 minutes. Compare your GPU utilization and costs before and after. No credit card required.
                        </p>
                        <Link
                            to="/trial"
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-colors group"
                        >
                            Start 14-Day Free Trial
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <p className="text-sm text-gray-500 mt-4">
                            Self-hosted -- No credit card -- 5-min setup
                        </p>
                    </div>
                </div>
            </section>

            <FooterSection />
        </div>
    );
};
